import { z } from "zod";
import { auditViewRowSchema } from "@amber/contracts";

export const PROFILE_HISTORY_CATEGORIES = ["all", "plan", "sources", "identity", "merges"] as const;
export const profileHistoryCategorySchema = z.enum(PROFILE_HISTORY_CATEGORIES);
export type ProfileHistoryCategory = z.infer<typeof profileHistoryCategorySchema>;

export const PROFILE_HISTORY_LABELS: Record<ProfileHistoryCategory, string> = {
  all: "All",
  plan: "Monitoring plan",
  sources: "Sources",
  identity: "Identity",
  merges: "Merges",
};

/** The audit actions each History filter shows; actions listed nowhere appear under All only. */
export const PROFILE_HISTORY_ACTIONS: Record<Exclude<ProfileHistoryCategory, "all">, readonly string[]> = {
  plan: [
    "profiles.tracking_started",
    "profiles.plan_recorded",
    "profiles.plan_applied",
    "profiles.recheck_finished",
    "profiles.person_paused",
    "profiles.person_resumed",
    "profiles.retired",
    "profiles.reactivated",
  ],
  sources: ["profiles.suggestion_approved", "profiles.suggestion_rejected", "collection.source_edited"],
  identity: [
    "profiles.profile_created",
    "profiles.identity_completed",
    "profiles.identity_edited",
    "profiles.refresh_decided",
    "profiles.draft_deleted",
    "profiles.profile_restored",
  ],
  merges: [
    "profiles.merge_started",
    "profiles.split_started",
    "profiles.merged",
    "profiles.merged_away",
    "profiles.split",
    "profiles.merge_reverse_started",
    "profiles.merge_reversed",
  ],
};

export const PROFILE_HISTORY_PAGE = 25;

export const profileHistoryQuerySchema = z.strictObject({
  category: profileHistoryCategorySchema.optional(),
  cursor: z.string().regex(/^\d+$/).optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
});
export type ProfileHistoryQuery = z.infer<typeof profileHistoryQuerySchema>;

const count = z.number().int().nonnegative();

export const profileHistoryCountsSchema = z.strictObject({
  all: count,
  plan: count,
  sources: count,
  identity: count,
  merges: count,
});

export const profileHistoryResponseSchema = z.strictObject({
  items: z.array(auditViewRowSchema),
  next_cursor: z.string().nullable(),
  total: z.number().int().nonnegative(),
  counts: profileHistoryCountsSchema,
});
export type ProfileHistoryResponse = z.infer<typeof profileHistoryResponseSchema>;
